import { ApiRequestError, completeTranscriptChunk, requestTranscriptChunkUrl } from '../lib/api';
import type { RecordedChunk } from './recorder';

type ChunkInput = { startMs: number; durationMs: number; mimeType: string; phoneLegId?: string };

/**
 * A feltoltes UTJA. Alapbol a bejelentkezett kolléga vegpontjai; a vendeg
 * (H2) a sajat, titkaval vedett vegpontjain tolt fel - ugyanigy, darabonkent.
 */
export interface ChunkTransport {
  requestUrl: (input: ChunkInput) => Promise<{ uploadUrl: string }>;
  complete: (input: ChunkInput) => Promise<unknown>;
}

/**
 * A hangdarabok feltoltese - SORBAN, egyesevel.
 *
 * Harom lepes darabonkent: helyet kerunk, feltoltjuk a tarolóba (a bajtok NEM
 * a szerveren mennek at), majd szolunk, hogy sorba allhat.
 *
 * **Miert sorban?** Egy rossz halozaton a parhuzamos feltoltesek egymast
 * lassitjak, es a hivas media-forgalmatol veszik el a savot - vagyis eppen a
 * beszelgetes minoseget rontanak azert, hogy legyen rola atirat. Egy darab par
 * tiz kilobajt; sorban is bosegesen tartja a lepest a felvetellel.
 *
 * Ami elveszik, az elveszett: az atirat egy szakasza hianyzik, a hivas viszont
 * nem all meg tole. De nem CSENDBEN veszik el - a hivo megkapja a hibat.
 */

/** Ennyiszer probaljuk ujra egy darabot, novekvo varakozassal. */
const MAX_ATTEMPTS = 3;

/**
 * Ezek nem hibak: a felvetel VEGET jelentik.
 *
 * Az atiratozas kikapcsolasa vagy a hivas lezarulasa utan a meg utban levo
 * darabot a szerver jogosan utasitja vissza. Ujraprobalni ertelmetlen, hibat
 * kiirni pedig megteveszto lenne.
 */
const STOPPED_CODES = new Set(['transcribe_off', 'transcribe_disabled', 'huddle_ended']);

/**
 * Ennel tobb darab nem varhat a sorban.
 *
 * Ha a halozat annyira rossz, hogy ennyi felgyulik, a REGIEKET dobjuk el: egy
 * elo hivasban a friss beszed a fontosabb, es a korlatlanul novo sor elobb-utobb
 * a bongeszo memoriajat vinne el.
 */
// Mondatonkenti darabolas mellett tobb, rovidebb darab van - a sor is hosszabb.
const MAX_QUEUE = 60;

export class ChunkUploader {
  private queue: RecordedChunk[] = [];
  private working = false;
  private dropped = 0;

  constructor(
    private readonly huddleId: string,
    private readonly onError: (message: string) => void,
    /** A telefonos fel hangja: nem a sajat mikrofonunk, hanem a hivo aga. */
    private readonly phoneLegId?: string,
    private readonly transport: ChunkTransport = {
      requestUrl: (input) => requestTranscriptChunkUrl(huddleId, input),
      complete: (input) => completeTranscriptChunk(huddleId, input),
    },
  ) {}

  /** Hany darab veszett el osszesen. A felulet ezt kiirhatja. */
  get droppedCount(): number {
    return this.dropped;
  }

  add(chunk: RecordedChunk): void {
    if (this.queue.length >= MAX_QUEUE) {
      this.queue.shift();
      this.note();
    }
    this.queue.push(chunk);
    void this.drain();
  }

  private note(): void {
    this.dropped += 1;
    // Egyszer szolunk, nem darabonkent: egy rossz halozaton kulonben a
    // hibauzenet maga valna zajja.
    if (this.dropped === 1) {
      this.onError('A hálózat nem bírja a hangdarabok feltöltését — az átirat hiányos lesz.');
    }
  }

  private async drain(): Promise<void> {
    if (this.working) return;
    this.working = true;

    try {
      while (this.queue.length > 0) {
        const chunk = this.queue.shift()!;
        const outcome = await this.send(chunk);

        /*
          AZ ATIRATOZAST KIKAPCSOLTAK - ez nem hiba.

          Amikor valaki a hivas kozben kikapcsolja az atiratot, a mar felvett
          darab meg utban lehet. A szerver ilyenkor jogosan utasitja vissza -
          es korabban ezt "a halozat nem birja a feltoltest" uzenettel
          jelentettuk, ami egyszerre volt ijeszto es hamis. A sort csendben
          eldobjuk: pontosan azt tesszuk, amit a felhasznalo kert.
        */
        if (outcome === 'stopped') {
          this.queue.length = 0;
          return;
        }

        if (outcome === 'failed') this.note();
      }
    } finally {
      this.working = false;
    }
  }

  private async send(chunk: RecordedChunk): Promise<'sent' | 'failed' | 'stopped'> {
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      try {
        /*
          A FORMATUM mindharom lepesben ugyanaz.

          Az elore alairt cim EGY tartalom-tipusra szol; ha a PUT masikat kuld,
          a tarolo elutasitja az alairast. A darab sorába pedig azert kell,
          hogy a felismero a helyes kiterjesztessel kapja meg - egy Ogg-darab
          `.webm` neven "Failed to decode audio."-t ad.
        */
        const { uploadUrl } = await this.transport.requestUrl({
          startMs: chunk.startMs,
          durationMs: chunk.durationMs,
          mimeType: chunk.mimeType,
          ...(this.phoneLegId ? { phoneLegId: this.phoneLegId } : {}),
        });

        const put = await fetch(uploadUrl, {
          method: 'PUT',
          body: chunk.blob,
          headers: { 'Content-Type': chunk.mimeType },
        });
        if (!put.ok) throw new Error(`a tároló ${put.status} választ adott`);

        await this.transport.complete({
          startMs: chunk.startMs,
          durationMs: chunk.durationMs,
          mimeType: chunk.mimeType,
          ...(this.phoneLegId ? { phoneLegId: this.phoneLegId } : {}),
        });
        return 'sent';
      } catch (error) {
        // Az atiratozas kozben kikapcsolt (vagy a hivas lezarult): nincs mit
        // ujraprobalni, es nincs mirol panaszkodni.
        if (error instanceof ApiRequestError && STOPPED_CODES.has(error.code)) return 'stopped';

        // Novekvo varakozas: egy pillanatnyi zavart kivarunk, egy tartos hibat
        // nem probalunk vegtelenul.
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** (attempt - 1)));
        }
      }
    }
    return 'failed';
  }
}
